/*
 * A set of native radios in a fieldset, with its own legend.
 *
 * It labels itself rather than sitting inside a Field, because `<label for>`
 * can only ever point at one element. Pointing the group's label at the first
 * radio makes "Priority" the name of the "Low" option, and the person is then
 * told they are choosing "Priority" when they are choosing between three
 * things. `<fieldset>` and `<legend>` are the elements that exist for exactly
 * this and are announced correctly everywhere.
 *
 * Native radios also bring the roving arrow-key behaviour for free: within a
 * group, Tab enters and leaves, and the arrows move the selection. A set of
 * buttons with `role="radio"` has to implement that by hand, and when it is
 * missing every option becomes its own tab stop, which is how a form with four
 * three-option questions turns into twelve tab presses instead of four.
 */

import { useId } from 'react'
import type { HTMLAttributes, ReactNode } from 'react'
import { joinIds } from '../aria'
import styles from './RadioGroup.module.css'

export interface RadioOption<T extends string> {
  value: T
  label: ReactNode
  /** A line under the option. For the ones whose consequence is not obvious. */
  hint?: ReactNode
  disabled?: boolean
}

export interface RadioGroupProps<T extends string>
  extends Omit<HTMLAttributes<HTMLFieldSetElement>, 'onChange' | 'defaultValue'> {
  legend: string
  /**
   * The form field name. Shared by every radio in the group, which is what
   * makes them one group to the browser rather than several checkboxes that
   * happen to be round.
   */
  name: string
  value: T | null
  options: ReadonlyArray<RadioOption<T>>
  /*
   * `NoInfer` so that `T` is decided by `value` and `options` alone.
   *
   * Without it the handler is an inference site too, and the common call
   * `onChange={setTiming}` passes a `Dispatch<SetStateAction<'now' | 'later'>>`
   * whose parameter is `'now' | 'later' | ((previous: ...) => ...)`. `T` then
   * widens to include the updater function, fails the `extends string` bound,
   * and resolves to `string`, so the group stops compiling against its own
   * options with an error that names neither the setter nor the union. Four
   * screens hit this and each worked around it differently: two wrote the type
   * argument out by hand, two wrapped the setter in an arrow. Fixing it here
   * means none of them had to.
   */
  onChange(next: NoInfer<T>): void
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  legendHidden?: boolean
  orientation?: 'vertical' | 'horizontal'
}

export function RadioGroup<T extends string>({
  legend,
  name,
  value,
  options,
  onChange,
  hint,
  error,
  required = false,
  legendHidden = false,
  orientation = 'vertical',
  className,
  ...rest
}: RadioGroupProps<T>) {
  const base = useId()
  const hintId = `${base}-hint`
  const errorId = `${base}-error`
  const invalid = error !== undefined && error !== null && error !== false

  const fieldsetClassName = [styles.group, className].filter(Boolean).join(' ')

  return (
    <fieldset
      {...rest}
      className={fieldsetClassName}
      aria-describedby={joinIds(rest['aria-describedby'], invalid && errorId, hint ? hintId : undefined)}
      aria-invalid={invalid || undefined}
      aria-required={required || undefined}
    >
      <legend className={[styles.legend, legendHidden ? 'visually-hidden' : ''].filter(Boolean).join(' ')}>
        {legend}
        {/* Decoration only. Required is announced from `aria-required` on the
            fieldset, for the reason set out in Field.tsx. */}
        {required ? (
          <span className={styles.requiredMark} aria-hidden="true">
            *
          </span>
        ) : null}
      </legend>

      {hint ? (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}

      <div
        className={[styles.options, orientation === 'horizontal' ? styles.horizontal : '']
          .filter(Boolean)
          .join(' ')}
      >
        {options.map((option) => {
          const optionId = `${base}-${option.value}`
          const optionHintId = `${optionId}-hint`
          return (
            <div className={styles.option} key={option.value}>
              <span className={styles.markWrap}>
                <input
                  type="radio"
                  className={styles.input}
                  id={optionId}
                  name={name}
                  value={option.value}
                  checked={option.value === value}
                  disabled={option.disabled}
                  onChange={() => onChange(option.value)}
                  aria-describedby={option.hint ? optionHintId : undefined}
                />
              </span>
              <span className={styles.text}>
                <label className={styles.label} htmlFor={optionId}>
                  {option.label}
                </label>
                {option.hint ? (
                  <span className={styles.optionHint} id={optionHintId}>
                    {option.hint}
                  </span>
                ) : null}
              </span>
            </div>
          )
        })}
      </div>

      {invalid ? (
        <p className={styles.error} id={errorId}>
          {error}
        </p>
      ) : null}
    </fieldset>
  )
}
